import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Loader from '@/components/Loader';
import {
  useHIWorkOrderByNumber,
  useHIInvoice,
  useHIWorkOrderComments,
} from '@/services/homeInspectionService';
import { useHIAttempts } from '@/services/homeInspectionAttemptService';
import { HIWorkOrderSidePanelHeader } from './HIWorkOrderSidePanelHeader';
import { HIWorkOrderSidePanelClientInfo } from './HIWorkOrderSidePanelClientInfo';
import { HIWorkOrderSidePanelServiceDetails } from './HIWorkOrderSidePanelServiceDetails';
import { HIWorkOrderSidePanelBilling } from './HIWorkOrderSidePanelBilling';
import { HIWorkOrderSidePanelAttempts } from './HIWorkOrderSidePanelAttempts';

type Props = {
  workOrderNumber: string;
  onClose: () => void;
};

const tabs = [
  { value: 'CLIENT_INFO',      label: 'Client Info' },
  { value: 'SERVICE_DETAILS',  label: 'Service Details' },
  { value: 'BILLING',          label: 'Billing' },
  { value: 'STATUS_UPDATES',   label: 'Status Updates' },
];

const HIWorkOrderSidePanel = ({ workOrderNumber, onClose }: Props) => {
  const { data: workOrder, isFetching, isError, refetch } = useHIWorkOrderByNumber(workOrderNumber);
  const workOrderId = workOrder?.workOrderId ?? null;
  const { data: invoice } = useHIInvoice(workOrderId);
  const { data: commentsData } = useHIWorkOrderComments(workOrderId);
  const { data: attempts } = useHIAttempts(workOrderId);

  const comments = commentsData?.comments ?? [];

  return (
    <Sheet modal={false} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white"
        side="right"
        tabIndex={-1}
      >
        <Loader isLoading={isFetching}>
          {isError ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder flex-col gap-4">
              <button
                type="button"
                aria-label="Close side panel"
                className="border-[1px] p-[6px] absolute top-6 right-6 rounded-md cursor-pointer"
                onClick={onClose}
              >
                <X color="#676767" className="h-[20px] w-[20px]" />
              </button>
              <span>FAILED TO LOAD WORK ORDER DETAILS</span>
              <button
                className="text-primary text-sm underline"
                onClick={() => refetch()}
              >
                Retry
              </button>
            </div>
          ) : workOrder ? (
            <Tabs className="w-full text-black" defaultValue={tabs[0].value}>
              <SheetHeader className="flex flex-col gap-4 pb-0 sticky top-0 bg-white">
                <SheetTitle className="flex flex-col gap-4 bg-white pb-0">
                  <HIWorkOrderSidePanelHeader
                    workOrder={workOrder}
                    attempts={attempts ?? []}
                    invoice={invoice}
                    onClose={onClose}
                  />
                </SheetTitle>
                <TabsList className="!mt-0 grid grid-cols-4">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="w-full"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </SheetHeader>

              <div className="w-full !mt-0 pb-2 text-[#0A0A0A]">
                <TabsContent value="CLIENT_INFO" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelClientInfo workOrder={workOrder} />
                </TabsContent>
                <TabsContent value="SERVICE_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelServiceDetails workOrder={workOrder} />
                </TabsContent>
                <TabsContent value="BILLING" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelBilling
                    invoice={invoice}
                    workOrderId={workOrder.workOrderId}
                    workOrderNumber={workOrder.workOrderNumber}
                    emailComments={comments}
                  />
                </TabsContent>
                <TabsContent value="STATUS_UPDATES" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <HIWorkOrderSidePanelAttempts
                    workOrderNumber={workOrder.workOrderNumber}
                    attempts={attempts ?? []}
                    emailComments={comments}
                  />
                </TabsContent>
              </div>
            </Tabs>
          ) : null}
        </Loader>
      </SheetContent>
    </Sheet>
  );
};

export default HIWorkOrderSidePanel;
